import type { LaunchReadinessOutput } from "@/features/tools/proToolTypes";

type LaunchReadinessResultProps = {
  output: LaunchReadinessOutput;
};

function formatBand(value: string): string {
  return value.replace(/_/g, " ");
}

function formatStatus(value: string): string {
  return value.replace(/_/g, " ");
}

function ListBlock({ items }: { items: string[] }) {
  if (items.length === 0) {
    return (
      <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">
        No items were returned for this section.
      </p>
    );
  }

  return (
    <ul className="grid gap-2 text-sm leading-6 text-slate-700">
      {items.map((item) => (
        <li key={item} className="min-w-0 break-words rounded-md bg-slate-50 p-3">
          {item}
        </li>
      ))}
    </ul>
  );
}

export function LaunchReadinessResult({
  output,
}: LaunchReadinessResultProps) {
  return (
    <section className="min-w-0 rounded-lg border border-emerald-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-700">
            Launch readiness result
          </p>
          <h3 className="mt-2 break-words text-2xl font-semibold tracking-normal text-slate-950">
            {output.readinessScore}/100
          </h3>
          <p className="mt-1 text-sm font-semibold capitalize text-slate-600">
            {formatBand(output.scoreBand)}
          </p>
        </div>
        <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Next step ready
        </div>
      </div>

      <p className="mt-5 whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">
        {output.summary}
      </p>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">
            Launch blockers
          </h4>
          <div className="mt-3">
            <ListBlock items={output.launchBlockers} />
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-950">
            Ready signals
          </h4>
          <div className="mt-3">
            <ListBlock items={output.readySignals} />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold text-slate-950">
          Recommended next step
        </h4>
        <p className="mt-2 text-sm leading-6 text-slate-700">
          {output.recommendedNextStep}
        </p>
      </div>

      <div className="mt-6">
        <h4 className="text-sm font-semibold text-slate-950">
          Score breakdown
        </h4>
        <div className="mt-3 grid gap-3">
          {output.scoreBreakdown.map((item) => (
            <div
              key={item.checkId}
              className="min-w-0 rounded-md border border-slate-200 bg-white p-4"
            >
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    {item.category}
                  </p>
                  <p className="mt-1 break-words text-sm font-semibold text-slate-950">
                    {item.question}
                  </p>
                </div>
                <p className="shrink-0 break-words text-sm font-semibold capitalize text-indigo-700">
                  {formatStatus(item.status)} - {item.scoreContribution} pts
                </p>
              </div>
              <p className="mt-2 break-words text-sm leading-6 text-slate-600">
                {item.interpretation}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
